// src/pages/BankStatementBalanceSheet.jsx
// ── KaiLux — Bank Statement Balance Sheet Completion ─────────────────────────
// Step 3 of the bank statement AFS flow.
// Receives P&L figures from BankStatement.jsx, collects year-end balance sheet
// figures from the accountant, merges both, navigates to /statements.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getOrCreateDraftJob, saveJobProgress } from '../api/kailux'
import BackButton from '../components/BackButton'

import Icon from '../components/Icon'

import { T, alpha, ink } from '../theme/theme'
export const fmt = (n) => n === 0 ? 'R 0' : `R ${Math.abs(n).toLocaleString('en-ZA', { minimumFractionDigits: 0 })}`

const BS_GROUPS = [
  {
    key: 'nca', label: 'NON-CURRENT ASSETS', color: T.bl,
    fields: [
      { key: 'Property Plant & Equipment',    label: 'PPE — cost at year-end',               hint: 'Total cost of all fixed assets before depreciation' },
      { key: 'Accumulated Depreciation',      label: 'Accumulated depreciation at year-end', hint: 'Total accumulated depreciation (positive number)' },
      { key: 'Depreciation and Amortisation', label: 'Depreciation charge for THIS year',    hint: 'Depreciation expense for the current year only' },
    ],
  },
  {
    key: 'ca', label: 'CURRENT ASSETS (year-end balances)', color: T.gn,
    fields: [
      { key: 'Inventories',               label: 'Inventories / stock at year-end',     hint: 'Closing stock count value' },
      { key: 'Trade and Other Receivables', label: 'Trade receivables (debtors)',         hint: 'Amounts owed by customers at year-end' },
      { key: 'Prepayments',               label: 'Prepayments and deposits',             hint: 'Advance payments not yet expensed' },
      { key: 'Tax Receivables',           label: 'Tax receivable (SARS refund due)',     hint: 'Provisional tax overpayment or VAT refund owing' },
    ],
  },
  {
    key: 'equity', label: 'EQUITY', color: T.or,
    fields: [
      { key: 'Stated Capital',      label: "Stated capital / members' contributions", hint: 'Share capital or initial contributions — from prior AFS or registration docs' },
      { key: 'Retained Earnings',   label: 'Retained earnings — OPENING balance',    hint: 'Accumulated profit / loss at START of this year (from prior year closing balance)' },
      { key: 'Income Tax Expense',  label: 'Income tax expense for the year',        hint: 'Current + deferred tax charge for this year — from the tax computation, not just cash paid to SARS' },
    ],
  },
  {
    key: 'ncl', label: 'NON-CURRENT LIABILITIES', color: T.pu,
    fields: [
      { key: 'Borrowings - Non-Current', label: 'Long-term borrowings (closing balance)', hint: 'Portion of loans due after 12 months at year-end — from bank / loan statement' },
      { key: 'Loans from Shareholders',  label: 'Shareholder / director loans',           hint: 'Amounts owed to shareholders — treat as non-current unless repayable within 12 months' },
    ],
  },
  {
    key: 'cl', label: 'CURRENT LIABILITIES (year-end balances)', color: T.rd,
    fields: [
      { key: 'Trade and Other Payables', label: 'Trade payables (creditors)',            hint: 'Amounts owed to suppliers at year-end — from creditors listing' },
      { key: 'Borrowings - Current',     label: 'Current portion of borrowings',         hint: 'Loan instalments due within next 12 months — from loan schedule' },
      { key: 'Tax Payables',             label: 'Income tax payable to SARS',            hint: 'Tax owed to SARS at year-end (leave 0 if refund due — use Tax Receivables above)' },
      { key: 'Deferred Revenue',         label: 'Deferred / unearned revenue',           hint: 'Advance payments received not yet earned at year-end' },
      { key: 'Accrued Liabilities',      label: 'Accruals (audit fee, leave pay etc.)',  hint: 'Expenses incurred but not yet invoiced at year-end' },
    ],
  },
]

const EMPTY_BS = () => {
  const obj = {}
  BS_GROUPS.forEach(g => g.fields.forEach(f => { obj[f.key] = '' }))
  return obj
}

export default function BankStatementBalanceSheet() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { firm }  = useAuth()
  const {
    client,
    mappings             = {},
    accounts             = [],
    afsJobId             = null,
    // FIX (Sep 2026 — bank-statement flow now routes through Review before
    // Statements): present when landing here via Review's/Statements' "Back"
    // button, carrying every figure this page previously collected so
    // nothing typed on this screen is lost. Not present on the normal
    // forward path from BankStatement.jsx, nor on a Resume (see bd below).
    figures: incomingFigures = null,
    // FEATURE (Sep 2026): which of the incoming figures above came from an
    // uploaded document (AdditionalDocuments.jsx, now positioned before
    // this screen — see BankStatement.jsx's own handleGenerateAFS) rather
    // than a plain hand-typed/carried-forward value. Used only for the
    // "from documents" badge on the relevant field labels below.
    documentSourcedKeys = [],
    // FIX (Sep 2026 — "Back to transactions" was forcing a full bank-
    // statement re-upload): opaque payload this page neither reads nor
    // modifies, only round-trips — see the matching comment in
    // BankStatement.jsx for what it restores and why. Present on the
    // normal forward path from BankStatement.jsx; also carried through
    // Review.jsx/Statements.jsx's own "Back" buttons so it survives a
    // Statements → Review → here → BankStatement round trip too.
    bs_snapshot          = null,
  } = location.state || {}

  // Draft-autosave fix: on a normal forward-navigation from BankStatement.jsx
  // every field below arrives directly on location.state, exactly as before.
  // On a RESUME (Dashboard/AFSHistory's handleResume, landing here via
  // STAGE_TO_ROUTE's 'bank_statement_balance_sheet' entry), those direct
  // keys aren't present — this page's whole prior-screen output instead
  // arrives inside the dedicated bank_statement_data blob (see
  // BankStatement.jsx and this page's own autosave effect below). Nullish-
  // coalescing rather than destructuring defaults, since a key that's
  // present-but-null (as several of these are on a resume, forwarded
  // generically by handleResume) would otherwise skip the default.
  const bd = location.state?.bank_statement_data || {}
  const plFigures                      = location.state?.plFigures                 ?? bd.plFigures                 ?? {}
  const prior_figures                  = location.state?.prior_figures             ?? bd.prior_figures             ?? {}
  const first_year                     = location.state?.first_year                ?? bd.first_year                ?? false
  const _openingCashProp               = location.state?.openingCash               ?? bd.openingCash               ?? 0
  const _netCashProp                   = location.state?.netCash                   ?? bd.netCash                   ?? 0
  const _directorDrawingsProp          = location.state?.directorDrawings          ?? bd.directorDrawings          ?? 0
  const _directorRemunerationProp      = location.state?.directorRemuneration      ?? bd.directorRemuneration      ?? 0
  const relatedPartyItems              = location.state?.relatedPartyItems         ?? bd.relatedPartyItems         ?? []
  // FEATURE (kailux-fixes-log.md §41 follow-up): tracked the same way as
  // directorDrawings — see BankStatement.jsx's buildFigures for why it's
  // kept distinct from both directorDrawings and relatedPartyItems.
  const _relatedPartyLoanMovementsProp = location.state?.relatedPartyLoanMovements ?? bd.relatedPartyLoanMovements ?? 0
  // FIX: tracked the same way as relatedPartyLoanMovements above — see
  // BankStatement.jsx's buildFigures for why loan_repay must never
  // silently vanish from figures with no signal to the accountant.
  // internal_transfer (below) is the multi-account feature's own tracked
  // total for confirmed transfers between the client's own linked
  // accounts — already excluded from the P&L there, just not previously
  // surfaced on this screen; wired through here the same way.
  const _loanRepaymentsProp            = location.state?.loanRepayments           ?? bd.loanRepayments           ?? 0
  const _internalTransferTotalProp     = location.state?.internalTransferTotal    ?? bd.internalTransferTotal    ?? 0
  const financialYear                  = location.state?.financialYear             ?? bd.financialYear             ?? String(new Date().getFullYear())
  const yearEndDate                    = location.state?.yearEndDate               ?? bd.yearEndDate               ?? ''

  // FIX (Sep 2026 — bank-statement flow now routes through Additional
  // Documents before this screen too, and through Review before
  // Statements): these three read-only figures and the cash split are
  // forwarded as part of the single `figures` object specifically on a
  // round trip back from Review/Statements (that object is the
  // round-trip source of truth those pages use) — NOT on the forward
  // pass from AdditionalDocuments.jsx, which spreads the real top-level
  // props straight through untouched but always attaches its own
  // `figures` key too (possibly {} if nothing was uploaded). Gating on
  // "incomingFigures is truthy" alone (as this used to) would wrongly
  // prefer an absent/undefined value from that mostly-empty object over
  // the real forwarded prop on every first-time visit. Check each actual
  // key instead, falling back to the prop when that specific key isn't
  // present in `figures`.
  const directorDrawings         = incomingFigures?.['Director Drawings']        ?? _directorDrawingsProp
  const directorRemuneration     = incomingFigures?.['Director Remuneration']    ?? _directorRemunerationProp
  const relatedPartyLoanMovements = incomingFigures?.['Related Party Loan Movements'] ?? _relatedPartyLoanMovementsProp
  const loanRepayments           = incomingFigures?.['Loan Repayments (Bank Stmt)']   ?? _loanRepaymentsProp
  const internalTransferTotal    = incomingFigures?.['Internal Transfers (Bank Stmt)'] ?? _internalTransferTotalProp
  const openingCash = incomingFigures?.['Cash and Cash Equivalents'] !== undefined ? 0 : _openingCashProp
  const netCash     = incomingFigures?.['Cash and Cash Equivalents'] ?? _netCashProp

  // FEATURE (kailux-fixes-log.md §41 follow-up — pre-fill as an editable
  // suggestion, not an automated posting): a confirmed related-party loan/
  // capital movement is money that left the company, so it's typically now
  // owed back — Trade and Other Receivables is the most defensible general
  // default across entity structures. This is a *starting suggestion* the
  // accountant can freely edit or move to Loans from Shareholders (see the
  // notice below) — never a silently-asserted fact, matching the pattern
  // already used for editable scratch defaults elsewhere (e.g. Workpapers'
  // initDebtors/initTax). Deliberately not extended to directorDrawings in
  // this pass — that would be a separate decision, not part of this fix.
  const initialBs = () => {
    if (bd.bs) return bd.bs // resumed session — restore exactly what was saved
    const base = EMPTY_BS()
    // Suggest the related-party loan movement into Receivables first (same
    // as always) — then layer any incoming figures on top. These never
    // collide on the same key (relatedPartyLoanMovements only ever touches
    // 'Trade and Other Receivables'; incoming figures here are either a
    // full round-trip from Review/Statements' Back button, or document-
    // sourced PPE/Borrowings fields from AdditionalDocuments.jsx — neither
    // ever sets Receivables), so applying both is safe and means an
    // uploaded Fixed Asset Register doesn't have to come at the cost of
    // this existing suggestion, or vice versa.
    if (relatedPartyLoanMovements > 0) {
      base['Trade and Other Receivables'] = String(relatedPartyLoanMovements)
    }
    // FIX (Sep 2026 — bank-statement flow now routes through Review before
    // Statements, and through Additional Documents before this screen):
    // landing here via "Back" from Review/Statements restores every
    // manually-entered field from the figures already collected, instead
    // of starting blank (the fix for the reported bug where going back
    // after being blocked by the disclosure-checklist gate wiped every
    // number that had been entered). Landing here forward from
    // AdditionalDocuments.jsx pre-fills whichever fields a Fixed Asset
    // Register / Loan Agreement produced (see documentSourcedKeys for
    // which ones, used for the "from documents" badge above). Guarded on
    // a non-empty object because AdditionalDocuments.jsx always attaches
    // a `figures` key now, even when nothing was uploaded — an empty {}
    // here means "nothing to restore", not "restore nothing".
    if (incomingFigures && Object.keys(incomingFigures).length > 0) {
      Object.keys(base).forEach(key => {
        if (incomingFigures[key] !== undefined && incomingFigures[key] !== null) {
          base[key] = String(incomingFigures[key])
        }
      })
      return base
    }
    return base
  }
  const [bs, setBs]         = useState(initialBs)
  const [activeHint, setHint] = useState(null)

  // ── Draft-autosave fix ──────────────────────────────────────────────────
  // Same mechanism as BankStatement.jsx's own autosave (see the comment
  // there for the full rationale) — this screen just needs its own copy
  // since it's a separate mounted component with its own field state (`bs`).
  // The draft job itself should already exist by the time a preparer
  // reaches this screen (created on the previous screen as soon as
  // transactions were uploaded) — ensureAfsJobId here is a fallback for the
  // unlikely case that earlier autosave never got a chance to fire (e.g. an
  // immediate navigation before the first debounce completed), not the
  // normal path.
  const resumedAfsJobIdRef    = useRef(afsJobId || null)
  const jobCreationPromiseRef = useRef(null)

  const ensureAfsJobId = async () => {
    if (resumedAfsJobIdRef.current) return resumedAfsJobIdRef.current
    if (!client?.id || !firm?.id) return null
    if (!jobCreationPromiseRef.current) {
      jobCreationPromiseRef.current = getOrCreateDraftJob(
        client.id, firm.id, financialYear, undefined, undefined, 'bank_statement'
      ).then((data) => {
        resumedAfsJobIdRef.current = data?.afs_job_id || null
        return resumedAfsJobIdRef.current
      }).catch((err) => {
        console.error('Bank statement draft job creation failed (non-fatal):', err)
        jobCreationPromiseRef.current = null
        return null
      })
    }
    return jobCreationPromiseRef.current
  }

  // Continuous autosave on every manual-entry field change, debounced.
  // Snapshots the FULL set of inputs this screen depends on (not just `bs`)
  // so a resume lands here with everything this component's own destructure
  // above needs, exactly as bank_statement_data already does for
  // BankStatement.jsx's screen.
  const autosaveTimerRef = useRef(null)
  useEffect(() => {
    if (!client?.id || !firm?.id) return
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(async () => {
      try {
        const jobId = await ensureAfsJobId()
        if (!jobId) return
        await saveJobProgress(jobId, {
          stage: 'bank_statement_balance_sheet',
          bank_statement_data: {
            ...bd, bs, plFigures, prior_figures, first_year,
            openingCash, netCash, directorDrawings, directorRemuneration,
            relatedPartyItems, relatedPartyLoanMovements,
            loanRepayments, internalTransferTotal,
            financialYear, yearEndDate,
          },
        })
      } catch (err) {
        console.error('Bank statement (balance sheet) autosave failed (non-fatal):', err)
      }
    }, 600)
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bs, client?.id, firm?.id])

  const setField = (key, val) => setBs(prev => ({ ...prev, [key]: val }))

  const n = (key) => {
    const raw = bs[key]
    if (raw === '' || raw === null || raw === undefined) return 0
    const v = parseFloat(String(raw).replace(/,/g, ''))
    return isNaN(v) ? 0 : v
  }

  const closingCash = openingCash + netCash
  const cashAsset      = Math.max(closingCash, 0)   // asset (0 when in overdraft)
  const bankOverdraft  = Math.max(-closingCash, 0)  // current liability when negative

  const plRevenue     = plFigures['Revenue'] || 0
  const plCogs        = plFigures['Cost of Sales'] || 0
  const plOpex        = ['Employee Costs','Occupancy Costs','Communication Expenses',
                         'Travel and Motor Expenses','General and Admin Expenses',
                         'Professional Fees','Sales and Marketing','Credit Losses']
                        .reduce((s, k) => s + (plFigures[k] || 0), 0)
  const plDepreciation = n('Depreciation and Amortisation')
  const plFinanceCosts = plFigures['Finance Costs'] || 0
  const plOtherInc    = plFigures['Other Income'] || 0
  const plTaxExpense  = n('Income Tax Expense')
  const plPBT          = plRevenue - plCogs + plOtherInc - plOpex - plDepreciation - plFinanceCosts
  const plPAT           = plPBT - plTaxExpense

  const ppeNet       = Math.max(n('Property Plant & Equipment') - n('Accumulated Depreciation'), 0)
  // FIX (Sep 2026 — director/shareholder loan sign bug): mirrors the fix in
  // backend/generate_afs.py — see that file for the full writeup. Previously
  // this page's own hint text told preparers to enter a negative number "so
  // the system will show it as a debtor", but nothing here (or in the
  // backend, before that fix) ever actually did that — a negative value was
  // just netted straight into Non-Current Liabilities, which is why this
  // live preview and the final PDF could each look "off" in different ways.
  const loansShNet     = n('Loans from Shareholders')
  const loansShLiability = Math.max(loansShNet, 0)
  const loansShDebtor    = Math.max(-loansShNet, 0)
  const totalNCA     = ppeNet + loansShDebtor
  const totalCA      = n('Inventories') + n('Trade and Other Receivables') +
                       n('Prepayments') + n('Tax Receivables') + cashAsset
  const totalAssets  = totalNCA + totalCA

  const totalEquity  = n('Stated Capital') + n('Retained Earnings') + plPAT
  const totalNCL     = n('Borrowings - Non-Current') + loansShLiability
  const totalCL      = n('Trade and Other Payables') + n('Borrowings - Current') +
                       n('Tax Payables') + n('Deferred Revenue') + n('Accrued Liabilities') +
                       bankOverdraft
  const totalEqLiab  = totalEquity + totalNCL + totalCL
  const bsDiff       = totalAssets - totalEqLiab
  const balanced     = Math.abs(bsDiff) < 10
  const closeEnough  = Math.abs(bsDiff) < 5000

  const computeMergedFigures = () => {
    const merged = {
      ...plFigures,
      'Property Plant & Equipment':    n('Property Plant & Equipment'),
      'Accumulated Depreciation':      n('Accumulated Depreciation'),
      'Depreciation and Amortisation': plDepreciation,
      'Inventories':                   n('Inventories'),
      'Trade and Other Receivables':   n('Trade and Other Receivables'),
      'Prepayments':                   n('Prepayments'),
      'Tax Receivables':               n('Tax Receivables'),
      'Stated Capital':                n('Stated Capital'),
      'Retained Earnings':             n('Retained Earnings'),
      'Borrowings - Non-Current':      n('Borrowings - Non-Current'),
      'Loans from Shareholders':       n('Loans from Shareholders'),
      'Trade and Other Payables':      n('Trade and Other Payables'),
      'Borrowings - Current':          n('Borrowings - Current'),
      'Tax Payables':                  n('Tax Payables'),
      'Deferred Revenue':              n('Deferred Revenue'),
      'Income Tax Expense':            plTaxExpense,
      // Actual cash value — may be negative (bank overdraft). generate_afs.py
      // routes negative values to current liabilities automatically.
      'Cash and Cash Equivalents':     closingCash,
      // Director and related party metadata for Note 18 disclosures
      'Director Remuneration':         directorRemuneration,
      'Director Drawings':             directorDrawings,
      // FEATURE (kailux-fixes-log.md §41 follow-up): surfaced for Note 18
      // disclosure only, same as Director Drawings above — this feature's
      // balance sheet screen (this page) is entirely manual entry, so
      // neither figure auto-posts to any balance sheet line. The accountant
      // sees this amount here and in the generated AFS's Note 18, and
      // reflects it in whichever manual field above is appropriate for the
      // client's actual related-party arrangement.
      'Related Party Loan Movements':  relatedPartyLoanMovements,
      // FIX (kailux-fixes-log.md §53 follow-up): now surfaced for Note 18
      // disclosure too, same as Director Drawings/Related Party Loan
      // Movements above (generate_afs.py's bank_statement_source branch) —
      // previously informational only, carried through the round-trip
      // with no note template consuming them.
      'Loan Repayments (Bank Stmt)':   loanRepayments,
      'Internal Transfers (Bank Stmt)': internalTransferTotal,
    }
    const accruals = n('Accrued Liabilities')
    if (accruals > 0) {
      merged['General and Admin Expenses'] = (merged['General and Admin Expenses'] || 0) + accruals
    }
    return merged
  }

  const handleGenerateAFS = () => {
    const merged = computeMergedFigures()
    // FIX (Sep 2026 — where should document upload sit in this flow?):
    // moved to run BEFORE this screen instead of after it (see
    // BankStatement.jsx's own handleGenerateAFS and
    // AdditionalDocuments.jsx's new figure-conflict handling) — an
    // uploaded Fixed Asset Register or Loan Agreement now pre-fills the
    // fields below directly (see incomingFigures above) rather than only
    // enriching note text once this screen's figures were already final.
    // This page therefore goes straight to Notes now, same as it did
    // before Additional Documents existed in this flow at all — Documents
    // already happened, upstream.
    navigate('/notes', {
      state: {
        client, mappings, accounts,
        figures: merged,
        prior_figures, first_year,
        prior_not_available: !first_year && Object.keys(prior_figures || {}).length === 0,
        source:                'bank_statement',
        bank_statement_source: true,
        financial_year:        financialYear,
        year_end:              yearEndDate,
        bs_snapshot,
        // Draft-autosave fix: forward the same draft job this whole pipeline
        // has been autosaving into, so /api/afs/generate's lookup-or-insert
        // by (client_id, financial_year) completes THIS draft in place
        // rather than the two ever diverging (in practice it already finds
        // the same row by that lookup regardless, but Statements.jsx's own
        // "already generated?" check keys off an explicit afsJobId first).
        afsJobId:              resumedAfsJobIdRef.current,
      }
    })
  }

  const diffColor = balanced ? T.gn : closeEnough ? T.yl : T.rd
  const diffLabel = balanced
    ? <><Icon name="check" /> Balanced</>
    : `Difference: R ${Math.abs(bsDiff).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`

  const inputStyle = {
    width: '100%', padding: '9px 10px 9px 28px',
    background: T.s3, border: `1px solid ${T.bd}`,
    borderRadius:'8px', color: T.tx, fontSize:'14px',
    fontFamily: "'Outfit',sans-serif", outline: 'none',
    boxSizing: 'border-box',
  }

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: "'Outfit',sans-serif", color: T.tx }}>

      {/* Nav */}
      <div style={{
        background: T.s1, borderBottom: `1px solid ${T.bd}`,
        padding: '0 32px', height: '62px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 40,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap:'16px' }}>
          {/* FIX (Sep 2026 — bank-statement flow now routes through
              Additional Documents before this screen, so "Back" from here
              goes there, not straight to BankStatement.jsx. That page's own
              upload/categorization progress lives only in its local state
              with nothing to restore it on remount (the "Back to
              transactions" wipeout, kailux-fixes-log.md §39/similar) — so
              bs_snapshot is still carried through explicitly here (in
              addition to the ...location.state spread) so it survives this
              hop and is available whenever the flow eventually lands back
              on BankStatement.jsx via history or a future Back button
              there, rather than being dropped now that Documents sits in
              between. */}
          <BackButton onClick={() => navigate('/additional-documents', {
            state: {
              ...(location.state || {}),
              client, mappings, accounts,
              figures: computeMergedFigures(),
              prior_figures, first_year,
              source: 'bank_statement', bank_statement_source: true,
              financial_year: financialYear, year_end: yearEndDate,
              bs_snapshot,
            }
          })} label="Back to Documents"
            textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '18px', fontWeight: '800' }}>
            Kai<span style={{ color: T.or }}>Lux</span>
          </div>
          {client && <span style={{ fontSize:'14px', color: T.mt }}>/ {client.name} / Balance Sheet</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap:'12px' }}>
          <div style={{
            padding: '6px 14px', borderRadius:'8px',
            background: balanced ? T.gnd : closeEnough ? alpha(T.am, 0.1) : alpha(T.rd, 0.1),
            border: `1px solid ${alpha(diffColor, 0.13)}`,
            fontSize:'13px', fontWeight: '700', color: diffColor,
          }}>
            {diffLabel}
          </div>
          <button onClick={handleGenerateAFS} disabled={!closeEnough} style={{
            padding: '9px 22px', borderRadius:'8px', border: 'none',
            background: closeEnough ? T.prg : T.bd,
            color: closeEnough ? T.onpri : T.mt,
            fontFamily: "'Outfit',sans-serif", fontSize:'14px',
            fontWeight: '700', cursor: closeEnough ? 'pointer' : 'not-allowed',
          }}>
            Continue to Notes →
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 24px 80px' }}>
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize:'12px', letterSpacing: '4px', color: T.or, fontWeight: '700', marginBottom: '10px' }}>
            BALANCE SHEET COMPLETION
          </div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '28px', fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '10px' }}>
            Complete the balance sheet
          </div>
          <div style={{ fontSize: '14px', fontWeight:'400', color:T.lt, lineHeight: '1.7', maxWidth: '620px' }}>
            The income statement is derived from the bank statement. Enter year-end balances
            below to complete the balance sheet. Cash is already computed from the statement.
          </div>
          {documentSourcedKeys.length > 0 && (
            <div style={{ marginTop:'16px', padding:'12px 16px', borderRadius:'8px',
              background: T.gnd, border:`1px solid ${alpha(T.gn, 0.25)}`, fontSize:'13px', color: T.tx,
              maxWidth:'620px', lineHeight:'1.6' }}>
              <strong>{documentSourcedKeys.length} field{documentSourcedKeys.length !== 1 ? 's' : ''} below
              {documentSourcedKeys.length !== 1 ? ' were' : ' was'} pulled from your uploaded documents</strong>{' '}
              (marked <span style={{ fontSize:'9px', fontWeight:700, color: T.gn, background:alpha(T.tx, 0.5),
              padding:'1px 6px', borderRadius:'999px' }}>from documents</span>) — please review and amend if needed.
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 310px', gap:'24px', alignItems: 'start' }}>

          {/* Input groups */}
          <div>
            {BS_GROUPS.map(group => (
              <div key={group.key} style={{ marginBottom: '24px' }}>
                <div style={{
                  fontSize: '10px', fontWeight: '700', color: group.color,
                  letterSpacing: '2px', marginBottom: '14px',
                  borderBottom: `1px solid ${T.bd}`, paddingBottom: '8px',
                }}>
                  {group.label}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap:'8px' }}>
                  {group.fields.map(field => (
                    <div key={field.key}>
                      <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: T.lt, letterSpacing: '0.5px', marginBottom: '4px' }}>
                        {field.label}
                        {documentSourcedKeys.includes(field.key) && (
                          <span title="Pulled from an uploaded document — review and amend if needed"
                            style={{ marginLeft:'6px', fontSize:'9px', fontWeight:700, color: T.gn,
                              background: T.gnd, padding:'1px 6px', borderRadius:'999px', letterSpacing:'normal' }}>
                            from documents
                          </span>
                        )}
                      </label>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize:'13px', color: T.mt, pointerEvents: 'none' }}>R</span>
                        <input type="number" value={bs[field.key]}
                          onChange={e => setField(field.key, e.target.value)}
                          onFocus={() => setHint(field.key)}
                          onBlur={() => setHint(null)}
                          placeholder="0"
                          style={{ ...inputStyle, borderColor: activeHint === field.key ? T.or : T.bd }}
                        />
                      </div>
                      {activeHint === field.key && (
                        <div style={{ fontSize: '10px', color: T.mt, marginTop: '3px', lineHeight: '1.4' }}>{field.hint}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Live balance summary */}
          <div style={{ position: 'sticky', top: '82px' }}>

            {/* P&L summary */}
            <div style={{ background: T.s1, border: `1px solid ${T.bd}`, borderRadius:'12px', padding:'16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: T.or, letterSpacing: '2px', marginBottom: '12px' }}>INCOME STATEMENT (from bank)</div>
              {[
                { label: 'Revenue',              value: plRevenue,                    color: T.gn },
                { label: 'Cost of sales',        value: -plCogs,                      color: T.rd },
                { label: 'Operating expenses',   value: -(plOpex + plDepreciation),   color: T.rd },
                { label: 'Finance costs',        value: -plFinanceCosts,              color: T.rd },
                { label: 'Other income',         value: plOtherInc,                   color: T.gn },
                { label: 'Profit before tax',    value: plPBT,                        color: plPBT >= 0 ? T.gn : T.rd },
                { label: 'Income tax expense',   value: -plTaxExpense,                color: T.rd },
                { label: 'Net profit / (loss) after tax', value: plPAT, bold: true,   color: plPAT >= 0 ? T.gn : T.rd },
              ].filter(r => r.value !== 0).map((row, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', padding:'3px 0', borderTop: row.bold ? `1px solid ${T.bd}` : 'none', marginTop: row.bold ? '6px' : 0, fontWeight: row.bold ? '700' : '400' }}>
                  <span style={{ color: T.mt }}>{row.label}</span>
                  <span style={{ color: row.color }}>{fmt(row.value)}</span>
                </div>
              ))}
            </div>

            {/* Balance sheet */}
            <div style={{ background: T.s1, border: `1px solid ${T.bd}`, borderRadius:'12px', padding:'16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: T.bl, letterSpacing: '2px', marginBottom: '12px' }}>BALANCE SHEET (live)</div>
              <div style={{ fontSize: '10px', color: T.lt, fontWeight: '700', marginBottom: '4px' }}>ASSETS</div>
              {[
                { label: 'PPE (net)',         value: ppeNet },
                { label: 'Inventories',       value: n('Inventories') },
                { label: 'Trade receivables', value: n('Trade and Other Receivables') },
                { label: 'Prepayments',       value: n('Prepayments') },
                { label: 'Tax receivables',   value: n('Tax Receivables') },
                { label: 'Cash',              value: closingCash },
              ].filter(r => r.value !== 0).map((row, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', padding:'2px 0' }}>
                  <span style={{ color: T.mt }}>{row.label}</span>
                  <span style={{ color: T.tx }}>{fmt(row.value)}</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'13px', fontWeight:'700', borderTop:`1px solid ${T.bd}`, marginTop:'6px', paddingTop:'6px' }}>
                <span style={{ color: T.bl }}>Total assets</span>
                <span style={{ color: T.bl }}>{fmt(totalAssets)}</span>
              </div>
              <div style={{ fontSize:'10px', color:T.lt, fontWeight:'700', marginTop:'14px', marginBottom:'4px' }}>EQUITY + LIABILITIES</div>
              {[
                { label: 'Stated capital',      value: n('Stated Capital') },
                { label: 'Opening retained',    value: n('Retained Earnings') },
                { label: 'Current year profit', value: plPAT,                        color: plPAT >= 0 ? T.gn : T.rd },
                { label: 'LT borrowings',       value: n('Borrowings - Non-Current') },
                { label: 'Shareholder loans',   value: n('Loans from Shareholders') },
                { label: 'Trade payables',      value: n('Trade and Other Payables') },
                { label: 'Current borrowings',  value: n('Borrowings - Current') },
                { label: 'Tax payable',         value: n('Tax Payables') },
                { label: 'Deferred revenue',    value: n('Deferred Revenue') },
                { label: 'Accruals',            value: n('Accrued Liabilities') },
              ].filter(r => r.value !== 0).map((row, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', padding:'2px 0' }}>
                  <span style={{ color: T.mt }}>{row.label}</span>
                  <span style={{ color: row.color || T.tx }}>{fmt(row.value)}</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'13px', fontWeight:'700', borderTop:`1px solid ${T.bd}`, marginTop:'6px', paddingTop:'6px' }}>
                <span style={{ color: T.or }}>Total equity + liab.</span>
                <span style={{ color: T.or }}>{fmt(totalEqLiab)}</span>
              </div>
              <div style={{ marginTop:'12px', padding:'10px 14px', borderRadius:'8px', background: balanced ? T.gnd : closeEnough ? alpha(T.am, 0.1) : alpha(T.rd, 0.1), border:`1px solid ${alpha(diffColor, 0.2)}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:'12px', color:T.mt }}>{balanced ? 'Balance sheet balances' : 'Difference to resolve'}</span>
                <span style={{ fontSize:'14px', fontWeight:'800', color:diffColor }}>{balanced ? <Icon name="check" /> : fmt(Math.abs(bsDiff))}</span>
              </div>
            </div>

            {/* Cash */}
            <div style={{ background: T.gnd, border:`1px solid ${alpha(T.gn, 0.2)}`, borderRadius:'12px', padding:'14px 16px' }}>
              <div style={{ fontSize:'10px', fontWeight:'700', color:T.gn, letterSpacing:'2px', marginBottom:'8px' }}>CASH (from bank statement)</div>
              {[
                { label: 'Opening balance', value: openingCash },
                { label: 'Net movement',    value: netCash },
                { label: closingCash < 0 ? 'Bank overdraft (closing)' : 'Closing balance',
                  value: closingCash, bold: true,
                  color: closingCash < 0 ? T.rd : T.gn },
              ].map((row, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', padding:'2px 0', borderTop: row.bold ? `1px solid ${alpha(T.gn, 0.2)}` : 'none', marginTop: row.bold ? '6px' : 0, fontWeight: row.bold ? '700' : '400' }}>
                  <span style={{ color:T.mt }}>{row.label}</span>
                  <span style={{ color: row.bold ? T.gn : T.tx }}>{fmt(row.value)}</span>
                </div>
              ))}
            </div>


            {/* Director drawings notification */}
            {directorDrawings > 0 && (
              <div style={{ marginTop:'12px', padding:'12px 14px',
                background:alpha(T.pu, 0.08), border:`1px solid ${alpha(T.pu, 0.25)}`,
                borderRadius:'10px' }}>
                <div style={{ fontSize:'11px', fontWeight:'700', color:T.pu,
                  marginBottom:'4px' }}>DIRECTOR DRAWINGS EXCLUDED FROM P&L</div>
                <div style={{ fontSize:'10px', color:T.lt, lineHeight:'1.6' }}>
                  R {directorDrawings.toLocaleString()} in director drawings was removed from
                  expenses. Use the <b>Loans from Shareholders</b> field to capture the net
                  balance of the director loan account at year-end. If the director owes money
                  to the company, enter it as a negative (the system will show it as a debtor).
                </div>
              </div>
            )}

            {/* Related-party loan/capital movement notification */}
            {relatedPartyLoanMovements > 0 && (
              <div style={{ marginTop:'12px', padding:'12px 14px',
                background:alpha(T.pu, 0.08), border:`1px solid ${alpha(T.pu, 0.25)}`,
                borderRadius:'10px' }}>
                <div style={{ fontSize:'11px', fontWeight:'700', color:T.pu,
                  marginBottom:'4px' }}>RELATED-PARTY LOAN MOVEMENT EXCLUDED FROM P&L</div>
                <div style={{ fontSize:'10px', color:T.lt, lineHeight:'1.6' }}>
                  R {relatedPartyLoanMovements.toLocaleString()} was confirmed as a related-party
                  loan/capital movement, not a genuine expense, and has been removed from expenses.
                  This company paid the money out, so it's typically now owed <b>back</b> to the
                  company — we've pre-filled <b>Trade and Other Receivables</b> with this amount as a
                  starting suggestion below; edit it if that's not the right figure. If the related
                  party is a shareholder/holding company, move it to a negative <b>Loans from
                  Shareholders</b> balance instead. Confirm the correct treatment for this client's
                  actual related-party structure before finalising — this is a suggested default, not
                  an automated determination.
                </div>
              </div>
            )}

            {/* Loan repayment notification */}
            {loanRepayments > 0 && (
              <div style={{ marginTop:'12px', padding:'12px 14px',
                background:alpha(T.mt, 0.08), border:`1px solid ${alpha(T.mt, 0.25)}`,
                borderRadius:'10px' }}>
                <div style={{ fontSize:'11px', fontWeight:'700', color:T.mt,
                  marginBottom:'4px' }}>LOAN REPAYMENTS EXCLUDED FROM P&L</div>
                <div style={{ fontSize:'10px', color:T.lt, lineHeight:'1.6' }}>
                  R {loanRepayments.toLocaleString()} in transactions were categorized as loan
                  repayments — this reduces borrowings, not profit, so it has been removed from
                  expenses. Make sure the <b>Borrowings — Current/Non-Current</b> fields above
                  already reflect this reduction; this is a reminder to reconcile, not an
                  automated adjustment.
                </div>
              </div>
            )}

            {/* Internal transfer notification — the confirmed transfer-
                between-own-accounts total (multi-account feature) wasn't
                previously surfaced on this screen at all; wired through the
                same way loan repayments now are. */}
            {internalTransferTotal > 0 && (
              <div style={{ marginTop:'12px', padding:'12px 14px',
                background:alpha('#F472B6', 0.08), border:`1px solid ${alpha('#F472B6', 0.25)}`,
                borderRadius:'10px' }}>
                <div style={{ fontSize:'11px', fontWeight:'700', color:ink('#F472B6'),
                  marginBottom:'4px' }}>INTERNAL TRANSFERS EXCLUDED FROM P&L</div>
                <div style={{ fontSize:'10px', color:T.lt, lineHeight:'1.6' }}>
                  R {internalTransferTotal.toLocaleString()} in transactions were confirmed as
                  transfers between this entity's own bank accounts — real income/expenses were
                  never involved, so these were excluded from the income statement entirely.
                  Confirm this treatment is correct for every flagged transaction before
                  finalising.
                </div>
              </div>
            )}

            {!closeEnough && (
              <div style={{ marginTop:'12px', padding:'12px 14px', background:alpha(T.rd, 0.08), border:`1px solid ${alpha(T.rd, 0.13)}`, borderRadius:'8px', fontSize:'12px', color:T.lt, lineHeight:'1.6' }}>
                Adjust your entries until the balance sheet difference is under R5,000 before generating the AFS.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
